// src/components/DownloadPage.jsx
// Post-purchase download page for Pecker Tap.
// Token-based auth via URL query param: /download?token=YOUR_TOKEN
// Add a route in App.jsx:
//   <Route path="/download" element={<DownloadPage />} />

import { useEffect, useRef, useState } from 'react'

const DMG_URL     = 'https://www.dropbox.com/scl/fi/x7litj7ivdn3eu23mjh29/Pecker-Tap-1.0.0-arm64.dmg?rlkey=yn8ai1le0m3eqrhgxxov9cake&st=4d5zjzno&dl=1'
const VALID_TOKEN = process.env.REACT_APP_DOWNLOAD_TOKEN || 'pt_7x9kQmP2vZ'

// ─── Particle canvas ──────────────────────────────────────────────────────────
function ParticleCanvas() {
  const ref = useRef(null)
  useEffect(() => {
    const canvas = ref.current
    const ctx = canvas.getContext('2d')
    let W, H, nodes = [], raf, last = 0

    function resize() {
      W = canvas.width  = window.innerWidth
      H = canvas.height = window.innerHeight
    }
    function init() {
      resize()
      nodes = Array.from({ length: 55 }, () => ({
        x: Math.random() * W, y: Math.random() * H,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        r: Math.random() * 1.8 + 0.6,
      }))
    }
    function draw(ts) {
      if (ts - last < 33) { raf = requestAnimationFrame(draw); return }
      last = ts
      if (document.hidden) { raf = requestAnimationFrame(draw); return }
      ctx.clearRect(0, 0, W, H)
      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i]
        a.x += a.vx; a.y += a.vy
        if (a.x < 0 || a.x > W) a.vx *= -1
        if (a.y < 0 || a.y > H) a.vy *= -1
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j]
          const dx = a.x - b.x, dy = a.y - b.y
          const dist = Math.sqrt(dx * dx + dy * dy)
          if (dist < 140) {
            ctx.beginPath()
            ctx.moveTo(a.x, a.y)
            ctx.lineTo(b.x, b.y)
            ctx.strokeStyle = `rgba(255,217,61,${(1 - dist / 140) * 0.12})`
            ctx.lineWidth = 0.5
            ctx.stroke()
          }
        }
        ctx.beginPath()
        ctx.arc(a.x, a.y, a.r, 0, Math.PI * 2)
        ctx.fillStyle = 'rgba(255,140,0,0.45)'
        ctx.fill()
      }
      raf = requestAnimationFrame(draw)
    }

    const onResize = () => {
      resize()
      nodes.forEach(n => { n.x = Math.min(n.x, W); n.y = Math.min(n.y, H) })
    }
    window.addEventListener('resize', onResize)
    init()
    raf = requestAnimationFrame(draw)
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', onResize) }
  }, [])

  return (
    <canvas ref={ref} style={{
      position: 'fixed', inset: 0, zIndex: 0,
      pointerEvents: 'none', opacity: 0.35,
    }} />
  )
}

// ─── Reusable pieces ─────────────────────────────────────────────────────────
function LiveDot() {
  return (
    <div style={{
      width: 6, height: 6, borderRadius: '50%',
      background: '#FFD93D', boxShadow: '0 0 8px #FFD93D',
      animation: 'dp-pulse 2s ease-in-out infinite',
    }} />
  )
}

function Code({ children, style }) {
  return (
    <span style={{
      display: 'inline-block',
      background: 'rgba(0,0,0,0.4)',
      border: '1px solid rgba(255,255,255,0.12)',
      borderRadius: 4, padding: '2px 6px',
      fontFamily: "'Press Start 2P', monospace",
      fontSize: 10, color: '#FFD93D',
      ...style,
    }}>{children}</span>
  )
}

function Step({ num, title, desc }) {
  return (
    <div style={{ display: 'flex', gap: 16, marginBottom: 20, alignItems: 'flex-start' }}>
      <div style={{
        width: 28, height: 28, borderRadius: 8, flexShrink: 0,
        background: 'rgba(255,217,61,0.1)', border: '1px solid rgba(255,217,61,0.3)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: "'Press Start 2P', monospace", fontSize: 11,
        fontWeight: 700, color: '#FFD93D',
      }}>{num}</div>
      <div style={{ flex: 1, paddingTop: 4 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', marginBottom: 4, fontFamily: "'DM Sans', sans-serif" }}>{title}</div>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.7, fontWeight: 300, fontFamily: "'DM Sans', sans-serif" }}>{desc}</div>
      </div>
    </div>
  )
}

function Divider() {
  return <div style={{ maxWidth: 600, width: '100%', height: 1, background: 'rgba(255,255,255,0.07)', margin: '40px 0' }} />
}

// ─── MAIN ────────────────────────────────────────────────────────────────────
export default function DownloadPage() {
  const [isDownloading, setIsDownloading] = useState(false)
  const [copiedCommand, setCopiedCommand] = useState(false)

  const urlParams = new URLSearchParams(window.location.search)
  const token = urlParams.get('token')
  const isAuthenticated = token === VALID_TOKEN
  
  // Debug: Log token validation
  console.log('DownloadPage Debug:')
  console.log('- URL search:', window.location.search)
  console.log('- Token from URL:', token)
  console.log('- VALID_TOKEN:', VALID_TOKEN)
  console.log('- Is authenticated:', isAuthenticated)

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedCommand(true)
      setTimeout(() => setCopiedCommand(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const handleDownload = () => {
    if (isDownloading) return
    setIsDownloading(true)
    
    // Trigger immediate download
    const link = document.createElement('a')
    link.href = DMG_URL
    link.download = 'Pecker-Tap-1.0.0-arm64.dmg'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    
    setTimeout(() => setIsDownloading(false), 3000)
  }

  // ── ACCESS RESTRICTED ────────────────────────────────────────────────────────
  if (!isAuthenticated) {
    return (
      <div style={{
        minHeight: '100vh', background: '#0D0F1A',
        color: '#fff', fontFamily: "'DM Sans', sans-serif",
        WebkitFontSmoothing: 'antialiased',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '60px 24px',
      }}>
        <link href="https://fonts.googleapis.com/css2?family=Press+Start+2P&family=DM+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

        <div style={{
          background: 'rgba(255,140,0,0.08)',
          border: '1px solid rgba(255,140,0,0.25)',
          borderRadius: 12, padding: 32,
          textAlign: 'center', maxWidth: 400,
        }}>
          <div style={{
            width: 64, height: 64, borderRadius: 16,
            background: 'rgba(255,140,0,0.1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#FF8C00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0110 0v4"/>
            </svg>
          </div>

          <h2 style={{
            fontFamily: "'Press Start 2P', monospace", fontSize: 14, lineHeight: 1.6,
            color: '#FF8C00', marginBottom: 12,
          }}>Access Restricted</h2>

          <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.6)', lineHeight: 1.6, marginBottom: 24 }}>
            This download page is only accessible after completing the purchase process.
          </p>

          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)' }}>
            Please complete your payment to access the Pecker Tap download.
          </p>
        </div>
      </div>
    )
  }

  // ── AUTHENTICATED — MAIN PAGE ─────────────────────────────────────────────
  return (
    <div style={{
      minHeight: '100vh', background: '#0D0F1A',
      color: '#fff', fontFamily: "'DM Sans', sans-serif",
      WebkitFontSmoothing: 'antialiased',
    }}>
      <link href="https://fonts.googleapis.com/css2?family=Press+Start+2P&family=DM+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

      <ParticleCanvas />

      {/* ── TOP BAR ── */}
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 10,
        height: 52, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 28px',
        background: 'rgba(13,15,26,0.88)', backdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(255,217,61,0.1)',
      }}>
        <span style={{
          fontFamily: "'Press Start 2P', monospace",
          fontSize: 14, color: '#FFD93D',
        }}>PeckerTap</span>
      </div>

      {/* ── PAGE CONTENT ── */}
      <div style={{
        position: 'relative', zIndex: 1,
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        padding: '120px 24px 80px',
      }}>

        {/* ── HERO ── */}
        <div style={{ maxWidth: 600, width: '100%', textAlign: 'center', marginBottom: 0 }}>

          {/* Badge */}
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            padding: '5px 14px', borderRadius: 20,
            background: 'rgba(255,217,61,0.1)', border: '1px solid rgba(255,217,61,0.3)',
            fontFamily: "'Press Start 2P', monospace", fontSize: 9,
            color: '#FFD93D', letterSpacing: '0.08em',
            marginBottom: 28, animation: 'dp-fadeup 0.6s ease both',
          }}>
            <LiveDot />
            Payment confirmed — thank you!
          </div>

          {/* Headline */}
          <h1 style={{
            fontSize: 'clamp(28px, 5vw, 48px)', fontWeight: 700,
            lineHeight: 1.1, letterSpacing: '-0.03em', marginBottom: 16,
            animation: 'dp-fadeup 0.6s 0.1s ease both', opacity: 0,
          }}>
            You're ready to{' '}
            <span style={{
              background: 'linear-gradient(90deg, #FFD93D, #FF8C00)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>start tapping.</span>
          </h1>

          <p style={{
            fontSize: 16, color: 'rgba(255,255,255,0.5)', lineHeight: 1.75,
            fontWeight: 300, marginBottom: 40,
            animation: 'dp-fadeup 0.6s 0.15s ease both', opacity: 0,
          }}>
            Your download is ready. Pecker Tap v1.0.0 for Apple Silicon Macs.
          </p>

          {/* ── DOWNLOAD BUTTON ── */}
          <div style={{ textAlign: 'center', marginTop: 40 }}>
            <button
              onClick={handleDownload}
              disabled={isDownloading}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 12,
                padding: '18px 36px',
                background: isDownloading
                  ? 'linear-gradient(135deg, #FF8C00, #FF6B35)'
                  : 'linear-gradient(135deg, #FFD93D, #FF8C00)',
                border: 'none', borderRadius: 14,
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 17, fontWeight: 700, color: '#0D0F1A',
                letterSpacing: '-0.01em',
                boxShadow: '0 0 40px rgba(255,217,61,0.25), 0 8px 32px rgba(0,0,0,0.4)',
                transition: 'transform 0.2s, box-shadow 0.2s',
                animation: 'dp-fadeup 0.6s 0.2s ease both', opacity: 0,
                cursor: isDownloading ? 'not-allowed' : 'pointer',
              }}
              onMouseEnter={e => {
                if (!isDownloading) {
                  e.currentTarget.style.transform = 'translateY(-2px)'
                  e.currentTarget.style.boxShadow = '0 0 60px rgba(255,217,61,0.35), 0 16px 48px rgba(0,0,0,0.5)'
                }
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = '0 0 40px rgba(255,217,61,0.25), 0 8px 32px rgba(0,0,0,0.4)'
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              {isDownloading ? 'Downloading...' : 'Download Pecker Tap'}
            </button>
          </div>

          {/* Meta */}
          <div style={{
            marginTop: 14, display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 10, fontFamily: "'Press Start 2P', monospace", fontSize: 9,
            color: 'rgba(255,255,255,0.25)', letterSpacing: '0.06em',
            animation: 'dp-fadeup 0.6s 0.25s ease both', opacity: 0,
            flexWrap: 'wrap',
          }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
              macOS 12+
            </span>
            <span style={{ opacity: 0.3 }}>·</span>
            <span>Apple Silicon (M1–M4)</span>
            <span style={{ opacity: 0.3 }}>·</span>
            <span>v1.0.0</span>
          </div>
        </div>

        <Divider />

        {/* ── INSTALL STEPS ── */}
        <div style={{ maxWidth: 600, width: '100%', animation: 'dp-fadeup 0.6s 0.35s ease both', opacity: 0 }}>
          <div style={{
            fontFamily: "'Press Start 2P', monospace", fontSize: 9, fontWeight: 600,
            color: '#FFD93D', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 20,
          }}>How to install</div>

          <Step num="1" title="Open the DMG file"
            desc={<>Double-click the <Code>Pecker-Tap-1.0.0-arm64.dmg</Code> file in your Downloads folder. A window will appear showing the Pecker Tap app icon.</>} />
          <Step num="2" title="Drag to Applications"
            desc={<>Drag the Pecker Tap icon into the <Code>Applications</Code> folder shortcut shown in that window. This installs the app on your Mac.</>} />
          <Step num="3" title="Launch Pecker Tap"
            desc="Open your Applications folder and double-click Pecker Tap. On first launch macOS may show a security warning — see below for how to handle it in seconds." />
          <Step num="4" title="Find it in your menu bar"
            desc="Pecker Tap lives in your macOS menu bar. Click the icon at the top of your screen to open the game window anytime." />
        </div>

        {/* ── GATEKEEPER BOX ── */}
        <div style={{
          maxWidth: 600, width: '100%',
          background: 'rgba(255,140,0,0.05)',
          border: '1px solid rgba(255,140,0,0.25)',
          borderRadius: 12, padding: '20px 22px',
          animation: 'dp-fadeup 0.6s 0.4s ease both', opacity: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8,
              background: 'rgba(255,140,0,0.12)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FF8C00" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#FF8C00', fontFamily: "'DM Sans', sans-serif" }}>
                About the macOS security warning
              </div>
              <div style={{ fontSize: 10, color: 'rgba(255,140,0,0.6)', fontFamily: "'Press Start 2P', monospace", marginTop: 2 }}>
                Gatekeeper · First launch only
              </div>
            </div>
          </div>

          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.8, fontWeight: 300, fontFamily: "'DM Sans', sans-serif" }}>
            <p style={{ marginBottom: 10 }}>
              When you first open Pecker Tap, macOS may say <em>"Pecker Tap cannot be opened because it is from an unidentified developer"</em> or <em>"Apple cannot check it for malicious software."</em>
            </p>
            <p style={{ marginBottom: 10 }}>
              This is normal for apps sold outside the Mac App Store. It does <strong style={{ color: '#fff' }}>not</strong> mean the app is unsafe — it simply means Pecker Tap is indie-distributed and not yet enrolled in Apple's $99/year notarization program.
            </p>
            <p style={{ marginBottom: 14 }}>
              To open it, use <strong style={{ color: '#fff' }}>any</strong> of these methods:
            </p>
            <p style={{ marginBottom: 10 }}>
              <strong style={{ color: '#fff' }}>Option A — Right-click method (easiest):</strong><br />
              Right-click (or Control-click) the Pecker Tap icon in Applications → click <Code>Open</Code> → click <Code>Open</Code> again in the dialog. You only need to do this once.
            </p>
            <p style={{ marginBottom: 10 }}>
              <strong style={{ color: '#fff' }}>Option B — System Settings:</strong><br />
              Go to <Code>System Settings → Privacy &amp; Security</Code>, scroll down, and click <Code>Open Anyway</Code> next to the Pecker Tap entry.
            </p>
            <p>
              <strong style={{ color: '#fff' }}>Option C — Terminal command (works on all versions including Sequoia):</strong><br />
              Open Terminal and run this command to remove the macOS quarantine flag:
              <div style={{ position: 'relative', display: 'inline-block', marginTop: 10 }}>
                <Code style={{ display: 'block', padding: '10px 50px 10px 12px', fontSize: 10, lineHeight: 1.8, color: '#FFD93D', borderColor: 'rgba(255,217,61,0.2)', background: 'rgba(0,0,0,0.5)', borderRadius: 6, cursor: 'pointer', userSelect: 'all' }}>
                  xattr -r -d com.apple.quarantine /Applications/Pecker\ Tap.app
                </Code>
                <button
                  onClick={() => copyToClipboard('xattr -r -d com.apple.quarantine /Applications/Pecker\\ Tap.app')}
                  style={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: copiedCommand ? '#5CBB3C' : '#FFD93D',
                    color: '#0D0F1A',
                    border: 'none',
                    borderRadius: 4,
                    padding: '6px 10px',
                    fontSize: 11,
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    boxShadow: copiedCommand ? '0 2px 8px rgba(92,187,60,0.3)' : '0 2px 8px rgba(255,217,61,0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-50%) scale(1.05)'
                    e.target.style.background = copiedCommand ? '#70D44A' : '#FFD93D'
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(-50%) scale(1)'
                    e.target.style.background = copiedCommand ? '#5CBB3C' : '#FFD93D'
                  }}
                >
                  {copiedCommand ? (
                    <>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      Copied!
                    </>
                  ) : (
                    <>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                      </svg>
                      Copy
                    </>
                  )}
                </button>
              </div>
            </p>
          </div>
        </div>

        <Divider />

        {/* ── FOOTER ── */}
        <div style={{
          maxWidth: 600, width: '100%', textAlign: 'center',
          fontFamily: "'Press Start 2P', monospace", fontSize: 9,
          color: '#FFD93D',
          display: 'flex', flexDirection: 'column', gap: 8,
          animation: 'dp-fadeup 0.6s 0.45s ease both', opacity: 0,
          lineHeight: 2,
        }}>
          <div>Pecker Tap v1.0.0 · macOS 12 Monterey or later · Apple Silicon</div>
          <div style={{ opacity: 0.4, color: '#fff' }}>PeckerTap™ © 2026. All rights reserved.</div>
        </div>

      </div>

      <style>{`
        @keyframes dp-pulse  { 0%,100%{opacity:1} 50%{opacity:.35} }
        @keyframes dp-fadeup { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:translateY(0)} }
      `}</style>
    </div>
  )
}
